import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, normalizeLangCode } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ScrollText, Download, Trash2, Pencil, CheckCircle2 } from "lucide-react";
import LodgeDatePicker from "../components/LodgeDatePicker";
import i18n from "../lib/i18n";

/**
 * Meeting Minutes — standalone page. Minutes are created as DRAFTS (no
 * signatures, no lodge stamp); the Approve button re-renders the PDF with
 * the officer signatures + the lodge stamp and publishes it to Documents.
 */
export default function MeetingMinutesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const uiLang = normalizeLangCode(i18n.language || "ro");
  const canDelete = user?.role === "admin" || user?.role === "superadmin";
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const [items, setItems] = useState([]);
  const [number, setNumber] = useState("");
  const [date, setDate] = useState(todayIso);
  const [text, setText] = useState("");
  const [subject, setSubject] = useState("");
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tab, setTab] = useState("draft");
  const [counts, setCounts] = useState({ draft: 0, approved: 0 });

  const load = async (prefill = false, filters = {}) => {
    try {
      const params = { status: filters.tab || tab };
      if (filters.q) params.q = filters.q;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      const { data } = await api.get("/minutes", { params });
      setItems(data.items || []);
      setCounts(data.counts || { draft: 0, approved: 0 });
      if (prefill) setNumber((cur) => (cur.trim() ? cur : (data.next_number || "1")));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(true); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const h = setTimeout(() => load(false, { q, dateFrom, dateTo, tab }), 400);
    return () => clearTimeout(h);
    /* eslint-disable-next-line */
  }, [q, dateFrom, dateTo, tab]);

  const switchTab = (next) => {
    setTab(next);
    setQ(""); setDateFrom(""); setDateTo("");
  };

  const downloadPdf = async (rec) => {
    try {
      const resp = await api.get(`/minutes/${rec.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${t("minutes.fileName", "proces-verbal")}-nr-${rec.number}-${rec.date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveDraft = async () => {
    if (!number.trim() || !date || !text.trim()) return;
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/minutes/${editId}`, { number: number.trim(), date, body_text: text, subject });
      } else {
        await api.post("/minutes", { number: number.trim(), date, body_text: text, subject, lang: uiLang, draft: true });
      }
      toast.success(t("minutes.draftSaved", "Ciornă salvată — fără semnături până la aprobare."));
      setText(""); setSubject(""); setEditId(null); setNumber("");
      await load(true);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const approve = async (rec) => {
    if (!window.confirm(t("minutes.approveConfirm", "Aprobi procesul-verbal nr. {{nr}}? Se aplică semnăturile ofițerilor și ștampila lojii.", { nr: rec.number }))) return;
    try {
      await api.post(`/minutes/${rec.id}/approve`);
      toast.success(t("minutes.approvedOk", "Aprobat — semnăturile și ștampila au fost aplicate."));
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startEdit = async (rec) => {
    try {
      const { data } = await api.get(`/minutes/${rec.id}`);
      setEditId(rec.id);
      setNumber(data.number || "");
      setDate(data.date || todayIso);
      setText(data.body_text || "");
      setSubject(data.subject || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (rec) => {
    if (!window.confirm(t("minutes.confirmDelete", "Ștergi procesul-verbal nr. {{nr}}?", { nr: rec.number }))) return;
    try {
      await api.delete(`/minutes/${rec.id}`);
      setItems((prev) => prev.filter((x) => x.id !== rec.id));
      if (editId === rec.id) { setEditId(null); setText(""); }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="minutes-page">
      <h1 className="page-title flex items-center gap-2">
        <ScrollText size={22} /> {t("minutes.title", "Procese-Verbale")}
      </h1>
      <p className="page-subtitle">{t("minutes.pageSubtitle", "Creează procesul-verbal ca ciornă, editează-l oricând, apoi aprobă-l — abia atunci se aplică semnăturile ofițerilor și ștampila lojii și se publică în Documente.")}</p>

      <div className="gold-card" style={{ padding: "1.5rem", marginTop: "1.5rem" }} data-testid="minutes-section">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-lodge">{t("minutes.number", "Număr")}</label>
            <input className="input-lodge" value={number} onChange={(e) => setNumber(e.target.value)} data-testid="minute-number" />
          </div>
          <div>
            <label className="label-lodge">{t("minutes.date", "Data")}</label>
            <LodgeDatePicker value={date} onChange={setDate} testId="minute-date" />
          </div>
          <div className="md:col-span-2">
            <label className="label-lodge">{t("minutes.subject", "Titlu")}</label>
            <input
              className="input-lodge" value={subject} maxLength={300}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={t("minutes.subjectPh", "ex. Ținuta de alegeri — iunie 2026")}
              data-testid="minute-subject"
            />
          </div>
          <div className="md:col-span-2">
            <label className="label-lodge">{t("minutes.body", "Textul procesului-verbal")}</label>
            <textarea
              className="input-lodge" rows={8} value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("minutes.bodyPlaceholder", "Astăzi, ... , s-au întrunit membrii Lojii ...")}
              data-testid="minute-body"
            />
          </div>
        </div>
        <div className="gold-divider my-4" />
        <div className="flex gap-2 flex-wrap">
          <button
            className="btn-primary-gold flex items-center gap-2"
            onClick={saveDraft}
            disabled={busy || !number.trim() || !date || !text.trim()}
            data-testid="minute-save-draft-btn"
          >
            <Pencil size={14} /> {busy ? "..." : editId ? t("minutes.updateDraft", "Actualizează ciorna") : t("minutes.saveDraft", "Salvează ciornă (fără semnături)")}
          </button>
          {editId && (
            <button className="btn-outline-gold" onClick={() => { setEditId(null); setText(""); load(true); }} data-testid="minute-cancel-edit">
              {t("common.cancel", "Anulează")}
            </button>
          )}
        </div>

        {(items.length > 0 || q || dateFrom || dateTo || counts.draft + counts.approved > 0) && (
          <>
            <div className="gold-divider my-4" />
            <div className="flex gap-2 mb-3" data-testid="minutes-tabs">
              <button
                className={tab === "draft" ? "btn-primary-gold" : "btn-outline-gold"}
                style={{ padding: "6px 14px", fontSize: "0.78rem" }}
                onClick={() => switchTab("draft")}
                data-testid="minutes-tab-draft"
              >
                📝 {t("minutes.tabDrafts", "Ciorne")} ({counts.draft})
              </button>
              <button
                className={tab === "approved" ? "btn-primary-gold" : "btn-outline-gold"}
                style={{ padding: "6px 14px", fontSize: "0.78rem" }}
                onClick={() => switchTab("approved")}
                data-testid="minutes-tab-approved"
              >
                ✅ {t("minutes.tabApproved", "Aprobate")} ({counts.approved})
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3" data-testid="minutes-filters">
              <input
                className="input-lodge" value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("minutes.searchPh", "Caută după titlu, număr sau emitent…")}
                data-testid="minutes-search"
              />
              <LodgeDatePicker value={dateFrom} onChange={setDateFrom} placeholder={t("minutes.fromDate", "De la data")} testId="minutes-date-from" />
              <div className="flex gap-2 items-center">
                <div style={{ flex: 1 }}>
                  <LodgeDatePicker value={dateTo} onChange={setDateTo} placeholder={t("minutes.toDate", "Până la data")} testId="minutes-date-to" />
                </div>
                {(q || dateFrom || dateTo) && (
                  <button className="btn-outline-gold" style={{ padding: "4px 10px" }}
                    onClick={() => { setQ(""); setDateFrom(""); setDateTo(""); }} data-testid="minutes-clear-filters">
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table className="lodge-table" data-testid="minutes-table">
                <thead>
                  <tr>
                    <th>{t("minutes.number", "Număr")}</th>
                    <th>{t("minutes.subject", "Titlu")}</th>
                    <th>{t("minutes.date", "Data")}</th>
                    <th>{t("minutes.status", "Stare")}</th>
                    <th>{t("minutes.issuedBy", "Emis de")}</th>
                    <th style={{ textAlign: "right" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: "10px 12px", fontStyle: "italic", color: "#a09a88" }} data-testid="minutes-no-results">—</td></tr>
                  )}
                  {items.map((rec) => {
                    const isDraft = rec.status === "draft";
                    return (
                      <tr key={rec.id} data-testid={`minute-row-${rec.id}`}>
                        <td style={{ fontWeight: 600 }}>{rec.number}</td>
                        <td style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={rec.subject || ""}>{rec.subject || "—"}</td>
                        <td>{rec.date?.split("-").reverse().join(".")}</td>
                        <td>
                          {isDraft ? (
                            <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#8a6d1a", background: "#f7efd8", padding: "2px 8px", borderRadius: 10 }} data-testid={`minute-draft-badge-${rec.id}`}>
                              {t("minutes.draft", "Ciornă")}
                            </span>
                          ) : (
                            <span style={{ fontSize: "0.68rem", fontWeight: 800, color: "#3e6a3c", background: "#e8f2e6", padding: "2px 8px", borderRadius: 10 }} data-testid={`minute-approved-badge-${rec.id}`}>
                              ✓ {t("minutes.approved", "Aprobat")}
                            </span>
                          )}
                        </td>
                        <td>{rec.issued_by}</td>
                        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                          {isDraft && (
                            <>
                              <button className="btn-primary-gold" style={{ padding: "4px 10px", marginRight: 6, fontSize: "0.72rem" }}
                                onClick={() => approve(rec)} data-testid={`minute-approve-${rec.id}`}>
                                <CheckCircle2 size={12} style={{ display: "inline", marginRight: 4 }} />{t("minutes.approve", "Aprobă")}
                              </button>
                              <button className="btn-outline-gold" style={{ padding: "4px 10px", marginRight: 6 }}
                                onClick={() => startEdit(rec)} data-testid={`minute-edit-${rec.id}`}>
                                <Pencil size={13} />
                              </button>
                            </>
                          )}
                          <button className="btn-outline-gold" style={{ padding: "4px 10px", marginRight: 6 }}
                            onClick={() => downloadPdf(rec)} data-testid={`minute-download-${rec.id}`}>
                            <Download size={13} />
                          </button>
                          {canDelete && (
                            <button className="btn-outline-gold" style={{ padding: "4px 10px", color: "#a33" }}
                              onClick={() => remove(rec)} data-testid={`minute-delete-${rec.id}`}>
                              <Trash2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
